import {
  getConsistencyUnregisteredXlsxUrl,
  getConsistencyUnusedXlsxUrl,
  useConsistencyUnregistered,
  useConsistencyUnused,
} from "@api/generated/consistency/consistency";
import type { Register, UnregisteredPlot } from "@api/generated/model";
import { useUserMe } from "@api/generated/user/user";
import { hasRole } from "@app/workspace";
import { keyColumn, LineLink } from "@features/genealogy/GenotypeParts";
import { keyText } from "@features/genealogy/genotype";
import { Loader, SegmentedControl } from "@mantine/core";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { fold, formatNumber } from "@ui/format";
import { PageHeader } from "@ui/PageHeader";
import { SearchBox } from "@ui/SearchBox";
import { codeText } from "@ui/table/columns";
import { type Column, DataTable, useDataTable } from "@ui/table/DataTable";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

const TABS = ["unused", "unregistered"] as const;
const REPORT_STALE = 5 * 60_000; // the lists change only with imports and merges

export const Route = createFileRoute("/genealogy/consistency")({
  validateSearch: z.object({ tab: z.enum(TABS).optional() }),
  component: ConsistencyPage,
});

/** Genealógia → Törzsek ellenőrzése (the legacy "Purge PAz-SID" verifier, admin): the registered lines nothing uses,
 *  and the plots / gene-bank accessions whose line is not registered (or that name none). Read only; each list is
 *  also a download. */
function ConsistencyPage() {
  const { t } = useTranslation();
  const tab = Route.useSearch().tab ?? "unused";
  const navigate = useNavigate({ from: Route.fullPath });
  const me = useUserMe().data;
  const admin = hasRole(me?.roles, "admin");
  if (!me) return <Loader size="sm" m="md" />;
  return (
    <div className="page fill">
      <PageHeader
        title={t("consistency.title")}
        subtitle={t("consistency.subtitle")}
        actions={admin && <span className="sm mu">{t("consistency.adminOnly")}</span>}
      />
      {!admin ? (
        <p className="mu">{t("consistency.noPermission")}</p>
      ) : (
        <section className="card stack grow" style={{ minHeight: 0, gap: 0 }}>
          <div className="hstack panel-head" style={{ gap: 10 }}>
            <SegmentedControl
              size="xs"
              value={tab}
              onChange={(v) => void navigate({ search: { tab: v as (typeof TABS)[number] } })}
              data={TABS.map((v) => ({ value: v, label: t(`consistency.tab.${v}`) }))}
            />
            <span className="grow sm mu">{t(`consistency.help.${tab}`)}</span>
            <a className="sm" href={tab === "unused" ? getConsistencyUnusedXlsxUrl() : getConsistencyUnregisteredXlsxUrl()}>
              {t("consistency.download")}
            </a>
          </div>
          {tab === "unused" ? <UnusedList /> : <UnregisteredList />}
        </section>
      )}
    </div>
  );
}

function Count({ shown, total }: { shown: number; total: number }) {
  const { t } = useTranslation();
  return (
    <p className="sm mu" style={{ margin: 0 }}>
      {shown < total
        ? t("consistency.truncated", { count: formatNumber(shown), total: formatNumber(total) })
        : t("consistency.count", { count: formatNumber(total) })}
    </p>
  );
}

function UnusedList() {
  const { t } = useTranslation();
  const data = useConsistencyUnused({ query: { staleTime: REPORT_STALE } }).data;
  const [q, setQ] = useState("");
  const lines = useMemo(() => {
    const wanted = fold(q);
    const all = data?.lines ?? [];
    return wanted
      ? all.filter((x) => fold(`${x.paz}/${x.sid} ${x.name ?? ""} ${x.torzs_szam ?? ""} ${x.pedigre ?? ""}`).includes(wanted))
      : all;
  }, [data, q]);
  const columns = useMemo<Column<Register>[]>(
    () => [
      // the key links to the line's sheet
      { ...keyColumn<Register>((x) => x), cell: (x) => <LineLink line={{ key: x, name: null }} /> },
      { id: "name", header: t("consistency.columns.name"), value: (x) => x.name },
      { id: "torzs_szam", header: t("register.field.torzs_szam"), value: (x) => x.torzs_szam },
      { id: "pedigre", header: t("register.pedigree"), value: (x) => x.pedigre, className: "sm mono" },
      {
        id: "register",
        header: "",
        name: t("consistency.register"),
        fixed: true,
        cell: (x) => (
          <Link className="sm" to="/genealogy/register" search={{ paz: x.paz, sid: x.sid }}>
            {t("consistency.register")}
          </Link>
        ),
      },
    ],
    [t],
  );
  // the server lists the first lines of its report (``total`` counts them all)
  const table = useDataTable("genealogy.consistency.unused", columns, lines, { partial: !!data && data.lines.length < data.total });
  if (!data) return <Loader size="sm" m="md" />;
  return (
    <>
      <div className="hstack inset" style={{ gap: 12 }}>
        <SearchBox value={q} onChange={setQ} placeholder={t("consistency.filter")} />
        <Count shown={lines.length} total={data.total} />
      </div>
      <DataTable table={table} rowKey={(x) => `${x.paz}/${x.sid}`} className="grow" />
    </>
  );
}

/** An unregistered plot or accession, numbered: two of them can share every shown value. */
type Unregistered = UnregisteredPlot & { at: number };

function UnregisteredList() {
  const { t } = useTranslation();
  const data = useConsistencyUnregistered({ query: { staleTime: REPORT_STALE } }).data;
  const rows = useMemo(() => data?.plots.map((p, at): Unregistered => ({ ...p, at })), [data]);
  const columns = useMemo<Column<Unregistered>[]>(
    () => [
      { id: "source", header: t("consistency.columns.source"), value: (p) => t(`consistency.source.${p.source}`) },
      { id: "year", header: t("consistency.columns.year"), value: (p) => p.ev, text: (p) => codeText(p.ev), numeric: true },
      {
        id: "location",
        header: t("consistency.columns.location"),
        value: (p) => [p.kis, p.parc].filter((v) => v != null).join(" "),
        className: "mono",
      },
      {
        id: "key",
        header: "PAz / SID",
        value: (p) => (p.paz == null || p.sid == null ? t("consistency.noLine") : keyText({ paz: p.paz, sid: p.sid })),
        className: "mono",
      },
    ],
    [t],
  );
  const table = useDataTable("genealogy.consistency.unregistered", columns, rows, { partial: !!data && data.plots.length < data.total });
  if (!data) return <Loader size="sm" m="md" />;
  return (
    <>
      <Count shown={data.plots.length} total={data.total} />
      <DataTable table={table} rowKey={(p) => p.at} className="grow" />
    </>
  );
}
